import Vue from 'vue'
import VueRouter from 'vue-router'

const Landing = () => import(/* webpackChunkName: "landing" */ '../views/Landing.vue')
const HomeView = () => import(/* webpackChunkName: "catalog" */ '../views/Home.vue')
const About = () => import(/* webpackChunkName: "about" */ '../views/About.vue')
const Admin = () => import(/* webpackChunkName: "admin" */ '../views/Admin.vue')
const Cart = () => import(/* webpackChunkName: "cart" */ '../views/Cart.vue')
const Equipment = () => import(/* webpackChunkName: "product" */ '../components/catalog/Equipment.vue')
const Partnership = () => import(/* webpackChunkName: "partnership" */ '../views/Partnership.vue')
const Profile = () => import(/* webpackChunkName: "profile" */ '../views/Profile.vue')
const Privacy = () => import(/* webpackChunkName: "legal" */ '../views/Privacy.vue')
const Agreement = () => import(/* webpackChunkName: "legal" */ '../views/Agreement.vue')
const Reviews = () => import(/* webpackChunkName: "reviews" */ '../views/Reviews.vue')
const Offer = () => import(/* webpackChunkName: "legal" */ '../views/Offer.vue')
const PersonalDataConsent = () => import(/* webpackChunkName: "legal" */ '../views/PersonalDataConsent.vue')
const MarketingConsent = () => import(/* webpackChunkName: "legal" */ '../views/MarketingConsent.vue')
const Returns = () => import(/* webpackChunkName: "legal" */ '../views/Returns.vue')
const Warranty = () => import(/* webpackChunkName: "legal" */ '../views/Warranty.vue')
const Requisites = () => import(/* webpackChunkName: "legal" */ '../views/Requisites.vue')
const Compliance = () => import(/* webpackChunkName: "legal" */ '../views/Compliance.vue')
const Contacts = () => import(/* webpackChunkName: "contacts" */ '../views/Contacts.vue')
const News = () => import(/* webpackChunkName: "news" */ '../views/News.vue')
const NewsArticle = () => import(/* webpackChunkName: "news" */ '../views/NewsArticle.vue')
const Favorites = () => import(/* webpackChunkName: "favorites" */ '../views/Favorites.vue')
const Login = () => import(/* webpackChunkName: "auth" */ '../views/Login.vue')
const Register = () => import(/* webpackChunkName: "auth" */ '../views/Register.vue')
const ForgotPassword = () => import(/* webpackChunkName: "auth" */ '../views/ForgotPassword.vue')
const ResetPassword = () => import(/* webpackChunkName: "auth" */ '../views/ResetPassword.vue')
const PaymentResult = () => import(/* webpackChunkName: "payment" */ '../views/PaymentResult.vue')
const NotFound = () => import(/* webpackChunkName: "not-found" */ '../views/NotFound.vue')

Vue.use(VueRouter)

const originalPush = VueRouter.prototype.push
const originalReplace = VueRouter.prototype.replace

function ignoreExpectedNavigationFailure(error, router) {
  const name = error && error.name ? String(error.name) : ''
  const message = String((error && error.message) || '')

  // Vue Router 3 rejects the promise not only for duplicated navigation,
  // but also when an in-flight navigation is superseded by a newer one.
  // Both cases are normal UI behaviour and must not become an uncaught error.
  const isExpected =
    name === 'NavigationDuplicated' ||
    name === 'NavigationCancelled' ||
    message.includes('Avoided redundant navigation') ||
    message.includes('Navigation cancelled from')

  if (isExpected) return router.currentRoute
  throw error
}

VueRouter.prototype.push = function push(location, onResolve, onReject) {
  if (onResolve || onReject) return originalPush.call(this, location, onResolve, onReject)
  return originalPush.call(this, location).catch(error => ignoreExpectedNavigationFailure(error, this))
}

VueRouter.prototype.replace = function replace(location, onResolve, onReject) {
  if (onResolve || onReject) return originalReplace.call(this, location, onResolve, onReject)
  return originalReplace.call(this, location).catch(error => ignoreExpectedNavigationFailure(error, this))
}

const routes = [
  { path: '/', name: 'home', component: Landing },
  { path: '/landing', redirect: '/' },
  { path: '/new-catalog', name: 'new-catalog', component: HomeView, props: { catalogExperiment: true }, meta: { noindex: true } },
  { path: '/about', name: 'about', component: About },
  { path: '/admin', name: 'admin', component: Admin },
  { path: '/catalog', name: 'catalog', component: HomeView },
  { path: '/catalog/:id', name: 'catalog-equipment', component: Equipment },
  { path: '/cart', name: 'cart', component: Cart },
  { path: '/payment/result', name: 'payment-result', component: PaymentResult },
  { path: '/profile', name: 'profile', component: Profile },
  { path: '/login', name: 'login', component: Login },
  { path: '/register', name: 'register', component: Register },
  { path: '/forgot-password', name: 'forgot-password', component: ForgotPassword },
  {
    path: '/reset-password',
    alias: '/reset-password/',
    name: 'reset-password',
    component: ResetPassword,
    props: route => ({
      resetToken: String(route.query.token || '').trim()
    })
  },
  { path: '/favorites', name: 'favorites', component: Favorites },
  { path: '/partnership', name: 'partnership', component: Partnership },
  { path: '/privacy', name: 'privacy', component: Privacy },
  { path: '/contacts', name: 'contacts', component: Contacts },
  { path: '/agreement', name: 'agreement', component: Agreement },
  { path: '/offer', name: 'offer', component: Offer },
  { path: '/personal-data-consent', name: 'personal-data-consent', component: PersonalDataConsent },
  { path: '/marketing-consent', name: 'marketing-consent', component: MarketingConsent },
  { path: '/returns', name: 'returns', component: Returns },
  { path: '/warranty', name: 'warranty', component: Warranty },
  { path: '/requisites', name: 'requisites', component: Requisites },
  { path: '/compliance', name: 'compliance', component: Compliance },
  { path: '/reviews/:id', name: 'reviews', component: Reviews },
  { path: '/news', name: 'news', component: News },
  { path: '/news/:slug', name: 'news-article', component: NewsArticle },
  { path: '*', name: 'not-found', component: NotFound }
]

const router = new VueRouter({
  hashbang: false,
  mode: 'history',
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) return savedPosition
    if (to.path === from.path && (to.path === '/' || to.path === '/catalog')) return false
    if (to.hash) return { selector: to.hash, offset: { x: 0, y: 130 } }
    return { x: 0, y: 0 }
  }
})

export default router
